class HeaderNavigation {
  static SLOT_BASE = {
    secondary: 'header-navigation__sub-panel',
    tertiary: 'header-navigation__sub-panel header-navigation__sub-panel-special-links',
  };

  static SPECIAL = 'header-navigation__sub-panel-special-links';

  static TRIGGER_SELECTOR =
    '.header-navigation-first-panel__child-trigger, .header-navigation-panel-footer__region-trigger';

  static OPEN_TRIGGER_SELECTOR =
    '.header-navigation-first-panel__child-trigger[aria-expanded="true"], .header-navigation-panel-footer__region-trigger[aria-expanded="true"]';

  constructor() {
    this.navResizeRAF = null;

    this.bindEvents();
    this.observeSpecialWidth();
  }

  // syncSpecialWidth() measures once, right after fill(). On the first open
  // Safari hasn't finished laying out the freshly cloned panel at that point,
  // so the measured width is too small and the panel's overflow-x: hidden
  // crops the image. Re-sync whenever a slot's real size settles.
  observeSpecialWidth() {
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const root = this.drawerOf(entry.target);

        if (root) {
          this.syncSpecialWidth(root);
        }
      });
    });

    document
      .querySelectorAll('.header-navigation__panel [data-nav-slot]')
      .forEach((slot) => observer.observe(slot));
  }

  drawerOf(el) {
    return el.closest('.header-navigation__panel');
  }

  slotsOf(root) {
    return {
      secondary: root.querySelector('[data-nav-slot="secondary"]'),
      tertiary: root.querySelector('[data-nav-slot="tertiary"]'),
    };
  }

  templateFor(root, slot, target) {
    return root.querySelector(
      `template[data-nav-template][data-slot="${slot}"][data-target="${CSS.escape(target)}"]`
    );
  }

  fill(slot, template, baseClass) {
    slot.replaceChildren(template.content.cloneNode(true));
    slot.className = baseClass;

    const mods = template.getAttribute('data-modifiers');
    if (mods) {
      slot.classList.add(...mods.split(/\s+/).filter(Boolean));
    }

    // Commit the closed state before adding `is-open`, otherwise the browser
    // collapses both into one frame and Safari never paints the panel.
    void slot.offsetWidth;
    slot.classList.add('is-open');
    slot.setAttribute('aria-hidden', 'false');

    slot.dispatchEvent(
      new CustomEvent('nav:hydrated', {
        bubbles: true,
      })
    );
  }

  clear(slot, baseClass) {
    slot.replaceChildren();
    slot.className = baseClass;
    slot.setAttribute('aria-hidden', 'true');
  }

  setTitle(root, text) {
    const el = root.querySelector('[data-panel-title]');

    if (el) {
      el.textContent = text ?? el.dataset.rootTitle ?? '';
    }
  }

  syncSpecialWidth(root) {
    const open = root.querySelector(
      '.header-navigation__sub-panel-special-links.is-open'
    );

    if (!open) {
      root.style.setProperty('--special-w', '0px');
      return;
    }

    const w = Math.ceil(open.getBoundingClientRect().width);

    root.style.setProperty('--special-w', `${w}px`);

    if (w === 0) {
      requestAnimationFrame(() => this.syncSpecialWidth(root));
    }
  }

  applyState(root, { secondaryIsSpecial, tertiaryOpen }) {
    root.classList.toggle('has-subpanel', !secondaryIsSpecial);
    root.classList.toggle(
      'has-special-links',
      secondaryIsSpecial || tertiaryOpen
    );

    root.style.setProperty(
      '--structural-cols',
      secondaryIsSpecial ? '3' : '6'
    );

    this.syncSpecialWidth(root);
  }

  showSecondary(root, secondaryTpl, button = null) {
    const { secondary, tertiary } = this.slotsOf(root);

    this.fill(
      secondary,
      secondaryTpl,
      HeaderNavigation.SLOT_BASE.secondary
    );

    const secondaryIsSpecial = secondary.classList.contains(
      HeaderNavigation.SPECIAL
    );

    const tertiaryTpl = secondaryTpl.hasAttribute('data-tertiary-target')
      ? this.templateFor(
          root,
          'tertiary',
          secondaryTpl.getAttribute('data-tertiary-target')
        )
      : null;

    let tertiaryOpen = false;

    if (tertiaryTpl) {
      this.fill(
        tertiary,
        tertiaryTpl,
        HeaderNavigation.SLOT_BASE.tertiary
      );
      tertiaryOpen = true;
    } else {
      this.clear(
        tertiary,
        HeaderNavigation.SLOT_BASE.tertiary
      );
    }

    this.applyState(root, {
      secondaryIsSpecial,
      tertiaryOpen,
    });

    if (button) {
      this.setTitle(
        root,
        button.dataset.subpanelTitle || button.textContent.trim()
      );
    }
  }

  collapse(root) {
    const { secondary, tertiary } = this.slotsOf(root);

    if (secondary) {
      this.clear(
        secondary,
        HeaderNavigation.SLOT_BASE.secondary
      );
    }

    if (tertiary) {
      this.clear(
        tertiary,
        HeaderNavigation.SLOT_BASE.tertiary
      );
    }

    root.classList.remove(
      'has-subpanel',
      'has-special-links'
    );

    root.style.setProperty('--structural-cols', '3');
    root.style.setProperty('--special-w', '0px');

    root
      .querySelectorAll(HeaderNavigation.OPEN_TRIGGER_SELECTOR)
      .forEach((b) => b.setAttribute('aria-expanded', 'false'));

    this.setTitle(root, null);
  }

  onTrigger(button) {
    const root = this.drawerOf(button);

    if (!root) return;

    const target = button.dataset.target;

    if (!target) return;

    if (button.getAttribute('aria-expanded') === 'true') {
      return this.collapse(root);
    }

    const secondaryTpl = this.templateFor(root, 'secondary', target);

    if (!secondaryTpl) return;

    root
      .querySelectorAll(HeaderNavigation.OPEN_TRIGGER_SELECTOR)
      .forEach((b) => b.setAttribute('aria-expanded', 'false'));

    button.setAttribute('aria-expanded', 'true');

    this.showSecondary(root, secondaryTpl, button);
  }

  openPanel(container) {
    document
      .querySelectorAll('.header-navigation__panel')
      .forEach((root) => this.collapse(root));

    const root = container.querySelector('.header-navigation__panel');

    if (!root) return;

    document
      .querySelectorAll('.header-navigation__container.active')
      .forEach((other) => {
        if (other === container) return;

        other.classList.remove('active');

        // This bypasses Modal.close() (switching panels shouldn't move focus
        // out to the old trigger), so mirror its aria-expanded cleanup here —
        // otherwise the trigger that opened `other` is left stuck at "true".
        if (other._opener) {
          other._opener.setAttribute('aria-expanded', 'false');
          other._opener = null;
        }

        const panel = other.querySelector('.modal__panel');

        if (panel?._removeFocusTrap) {
          panel._removeFocusTrap();
        }
      });

    // Modal.open already portalled this container to <body> and added `.active`
    // (synchronously, before this setTimeout-deferred method runs) — and that's
    // what drives the slide-in transition + focus trap. Re-appending here would
    // re-insert the node mid-transition and cancel the animation, so we only do
    // the nav-specific work below.

    const auto = root.querySelector(
      'template[data-nav-template][data-slot="secondary"][data-auto-open]'
    );

    if (auto) {
      this.showSecondary(root, auto);
    }
  }

  closePanel(container) {
    const root = container && container.querySelector('.header-navigation__panel');
    if (!root) return;
    let done = false;
    const reset = () => {
      if (done) return;
      done = true;
      collapse(root);
    };
    const onEnd = (ev) => {
      if (ev.propertyName !== 'transform') return;
      root.removeEventListener('transitionend', onEnd);
      if (!container.classList.contains('active')) reset();
    };
    root.addEventListener('transitionend', onEnd);
    setTimeout(reset, 600); // fallback if no transition fires
  }

  suppressPanelTransition(...ids) {
    const panels = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean)
      .map((c) => c.querySelector('.header-navigation__panel'))
      .filter(Boolean);
    panels.forEach((p) => p.classList.add('modal__panel--no-transition'));
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        panels.forEach((p) => p.classList.remove('modal__panel--no-transition'))
      )
    );
  }

  bindEvents() {
    document
      .querySelectorAll('[data-drawer-open^="HeaderMenuPanel-"]')
      .forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.drawerOpen;

          const current = document.querySelector(
            '.header-navigation__container.active'
          );

          if (current && current.id !== id) {
            this.suppressPanelTransition(current.id, id);
          }

          setTimeout(() => {
            const container = document.getElementById(id);

            if (container) {
              this.openPanel(container);
            }
          });
        });
      });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(
        HeaderNavigation.TRIGGER_SELECTOR
      );

      if (trigger?.hasAttribute('data-target')) {
        return this.onTrigger(trigger);
      }

      if (
        e.target.closest(
          '[data-subpanel-back], .header-navigation__panel-return'
        )
      ) {
        const root = this.drawerOf(e.target);

        if (root) {
          return this.collapse(root);
        }
      }

      if (
        e.target.closest('[data-drawer-close]') ||
        e.target.closest('.modal__backdrop')
      ) {
        const container =
          e.target.closest('.header-navigation__container') ||
          document.querySelector('.header-navigation__container.active');

        this.closePanel(container);
      }
    });

    document.addEventListener('keyup', (e) => {
      if (e.key !== 'Escape') return;

      document
        .querySelectorAll('.header-navigation__container.active')
        .forEach((container) => {
          container.classList.remove('active');
          document.body.classList.remove('modal-open');

          this.closePanel(container);
        });
    });

    window.addEventListener('resize', () => {
      cancelAnimationFrame(this.navResizeRAF);

      this.navResizeRAF = requestAnimationFrame(() => {
        document
          .querySelectorAll(
            '.header-navigation__panel.has-special-links'
          )
          .forEach((root) => this.syncSpecialWidth(root));
      });
    });
  }
}

new HeaderNavigation();
